import { t } from 'i18next';

import * as IngredientState from 'components/desktop/Ingredient/IngredientState';
import { IngredientDetailData } from 'components/desktop/Ingredient/useIngredientData';
import { IconsBar } from 'components/desktop/Recipe/IconsBar/IconsBar';
import Co2Display from 'components/desktop/_general/Co2Display/Co2Display';
import { RenderIf } from 'components/desktop/_general/Conditional/RenderIf';
import DetailHeader from 'components/desktop/_general/DetailHeader/DetailHeader';
import NutriScoreDiagram from 'components/desktop/_general/NutriScoreDiagram/NutriScoreDiagram';
import SeasonalityBar from 'components/desktop/_general/SeasonalityBar/SeasonalityBar';
import ENTITLEMENTS from 'enums/entitlements';
import { ValueScope } from 'enums/valueScope';
import getSeasonalityCode from 'functions/getSeasonalityCode';
import { getPriceString } from 'functions/numberToString';
import { getValueScopeLabel } from 'functions/valueScopeConversion';
import useCultureCode from 'hooks/redux/useCultureCode';
import useCurrency from 'hooks/redux/useCurrency';
import * as AllergenList from 'types/_general/Store/AllergenList';
import * as SeasonList from 'types/_general/Store/SeasonList';
import * as TagList from 'types/_general/Store/TagList';
import * as Translation from 'types/_general/Translation';

interface IProps {
	state: IngredientState.Type;
	data: IngredientDetailData;
	handleSave: () => void;
	isLoading: boolean;
	readOnly: boolean;
}

const IngredientHeader = ({ state, data, ...props }: IProps) => {
	const currency: string = useCurrency();
	const cultureCode: string = useCultureCode();

	const getTitle = (): string => {
		const text: string = Translation.getFirstTranslation(state.nameSingular, cultureCode);
		if (text) return text;
		if (!state.id) return t('ingredient:NEW_INGREDIENT');
		if (props.readOnly) return t('ingredient:VIEW_INGREDIENT');
		return t('ingredient:EDIT_INGREDIENT');
	};

	const renderBottomArea = (): JSX.Element => {
		return (
			<div
				className="d-flex flex-row justify-content-between align-items-end flex-grow-1"
				style={{ gap: '15px' }}
			>
				<IconsBar
					tags={TagList.getFromIdArray(data.tags, state.tagIds)}
					allergens={AllergenList.getFromIdArray(data.allergens, state.allergenIds)}
				/>

				<div className="d-flex" style={{ gap: '15px' }}>
					<RenderIf
						condition={Boolean(state.hasNutriScore && state.nutriScore)}
						entitlements={[ENTITLEMENTS.NUTRI_SCORE]}
					>
						<div className="d-flex flex-gap-25">
							{state.nutriScore && (
								<NutriScoreDiagram nutriScore={state.nutriScore} />
							)}
						</div>
					</RenderIf>

					<RenderIf condition={Boolean(state.price)} entitlements={[ENTITLEMENTS.PRICE]}>
						<div className="d-flex flex-gap-25">
							{state.price && (
								<div>
									<label style={{ whiteSpace: 'nowrap' }}>
										{getValueScopeLabel(
											ValueScope.HundredGrams,
											state.characteristic
										)}
									</label>
									<div
										className="form-control"
										style={{ whiteSpace: 'nowrap', fontWeight: 'bold' }}
									>
										{currency + ' ' + getPriceString(state.price, cultureCode)}
									</div>
								</div>
							)}
						</div>
					</RenderIf>

					<RenderIf entitlements={[ENTITLEMENTS.CARBONDIOXIDE]}>
						<Co2Display
							value={state.carbonDioxideValue}
							color={state.carbonDioxideLabel?.color || '#000'}
							fluid={state.characteristic}
							valueScopeInfo={null}
						/>
					</RenderIf>

					<RenderIf entitlements={[ENTITLEMENTS.SEASON]}>
						<SeasonalityBar
							seasonalityCode={getSeasonalityCode(
								SeasonList.getFromIdArray(data.seasons, state.seasonIds)
							)}
						/>
					</RenderIf>
				</div>
			</div>
		);
	};

	return (
		<DetailHeader
			handleSave={props.handleSave}
			title={getTitle()}
			image={state.image}
			isLoading={props.isLoading}
			readOnly={props.readOnly}
			bottomArea={renderBottomArea()}
		/>
	);
};

export default IngredientHeader;
